import { Route, Routes, BrowserRouter, useLocation, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import ErrorBoundary from "@/components/ErrorBoundary";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import HowItWorks from "./pages/HowItWorks";
import Contact from "./pages/Contact";
import Pricing from "./pages/Pricing";
import About from "./pages/About";
import Signup from "./pages/Signup";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import ResetPasswordConfirm from "./pages/ResetPasswordConfirm";
import AccountDeactivated from "./pages/AccountDeactivated";
import AuthDegraded from "./pages/AuthDegraded";
import PostLogin from "./pages/PostLogin";
import InvitedUserSetup from "./pages/InvitedUserSetup";
import PaymentVerification from "./pages/PaymentVerification";
import ClientOnboarding from "./components/ClientOnboarding";
import ClientPortal from "./components/ClientPortal";
import ClientProfile from "./components/ClientProfile";
import ClientPlan from "./components/ClientPlan";
import StartWorkout from "./components/StartWorkout";
import WorkoutSession from "./components/WorkoutSession";
import ExploreHub from "./components/ExploreHub";
import ViewWorkoutTemplate from "./components/ViewWorkoutTemplate";
import LessonDetailPage from "./components/LessonDetailPage";
import ClientChat from "./components/ClientChat";
import AdminPortal from "./components/AdminPortal";
import AssessmentEmbedPage from "./pages/AssessmentEmbedPage";
import { MobileNavbar, getActiveTabFromPath } from "./components/MobileNavbar";
import { ActiveWorkoutIndicator } from "./components/ActiveWorkoutIndicator";
import { PostWorkoutFeedback } from "./components/PostWorkoutFeedback";
import { AuthProvider } from "./contexts/AuthContext";
import { useAuth } from "./hooks/useAuth";
import { useSubscriptionStatus } from "./hooks/useSubscriptionStatus";
import { WorkoutProvider, useWorkout } from "./contexts/WorkoutContext";
import { useEffect, useState } from "react";
import { useIsMobile } from "./hooks/use-mobile";
import PaymentRequired from "./pages/PaymentRequired";
import { applyDocumentMeta } from "@/lib/page-titles";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 30 * 60 * 1000,
    },
  },
});

// Component to scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Post Workout Feedback Wrapper Component
const PostWorkoutFeedbackWrapper = () => {
  const {
    showPostWorkoutFeedback,
    hidePostWorkoutFeedbackDialog,
    submitPostWorkoutFeedback,
    activeWorkout,
  } = useWorkout();

  return (
    <PostWorkoutFeedback
      isOpen={showPostWorkoutFeedback}
      onClose={hidePostWorkoutFeedbackDialog}
      onSubmit={submitPostWorkoutFeedback}
      workoutTitle={activeWorkout?.title}
    />
  );
};

// Protected Route Component
const Loader = () => (
  <div className="min-h-screen gradient-dark flex items-center justify-center">
    <div className="text-white text-xl">Loading...</div>
  </div>
);

// Onboarding Route Component - redirects admins to /admin
const OnboardingRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isAdmin, loading, sessionChecked, rolesLoaded, rolesError, rolesFromCache } = useAuth();

  if (loading || !sessionChecked) {
    return <Loader />;
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  // Wait for roles to load before making redirect decision
  if (!rolesLoaded && !rolesError) {
    return <Loader />;
  }
  if (rolesError && !rolesFromCache) {
    return <Navigate to="/auth-degraded" replace />;
  }
  // Redirect admins to /admin, regardless of onboarding status
  if (isAdmin) {
    return <Navigate to="/admin" replace />;
  }
  return <>{children}</>;
};

const ProtectedRoute = ({
  children,
  requireOnboarding = false,
  disallowAdmin = false,
}: {
  children: React.ReactNode;
  requireOnboarding?: boolean;
  disallowAdmin?: boolean;
}) => {
  const { user, profile, loading, sessionChecked, isAdmin, rolesLoaded, rolesError, rolesFromCache } = useAuth();

  if (loading || !sessionChecked) {
    console.log("[route] ProtectedRoute → loader", { loading, sessionChecked });
    return <Loader />;
  }
  if (!user) {
    console.log("[route] ProtectedRoute → /login");
    return <Navigate to="/login" replace />;
  }
  // Optionally prevent admins from accessing client-only routes
  if (disallowAdmin) {
    // Wait for roles to resolve before making a redirect decision
    if (!rolesLoaded && !rolesError) {
      console.log("[route] ProtectedRoute(disallowAdmin) → loader (roles pending)");
      return <Loader />;
    }
    // If roles are degraded and we don't have a cached view, respect degraded auth routing elsewhere
    if (rolesError && !rolesFromCache) {
      console.log("[route] ProtectedRoute(disallowAdmin) → /auth-degraded");
      return <Navigate to="/auth-degraded" replace />;
    }
    if (isAdmin) {
      console.log("[route] ProtectedRoute(disallowAdmin) → /admin");
      return <Navigate to="/admin" replace />;
    }
  }
  if (requireOnboarding && profile && !profile.onboarding_completed) {
    console.log("[route] ProtectedRoute → /onboarding");
    return <Navigate to="/onboarding" replace />;
  }
  if ((profile as any)?.status === "deactivated") {
    console.log("[route] ProtectedRoute → /account-deactivated");
    return <Navigate to="/account-deactivated" replace />;
  }
  return <>{children}</>;
};

// Legal Approval Route Guard - redirects to legal tab if approval needed
const LegalApprovalRoute = ({ children }: { children: React.ReactNode }) => {
  const { profile, isClient, loading, sessionChecked } = useAuth();
  const location = useLocation();
  const [needsLegal, setNeedsLegal] = useState<boolean | null>(null);

  useEffect(() => {
    console.log("[route] LegalApprovalRoute check", {
      path: location.pathname,
      search: location.search,
      isClient,
      loading,
      sessionChecked,
      profileFlag: profile?.legal_document_approval_needed,
      needsLegal,
    });
    // Primary guard: profile flag
    if (profile?.legal_document_approval_needed && isClient) {
      setNeedsLegal(true);
      return;
    }
    // Fallback: fetch legal status once when client and no flag
    let active = true;
    if (isClient && profile && needsLegal === null) {
      (async () => {
        try {
          const status = await getLegalStatus();
          if (!active) return;
          const anyUnaccepted = status.some((item) => !item.is_accepted);
          setNeedsLegal(anyUnaccepted);
        } catch (err) {
          console.warn("[route] legal status check failed", err);
          if (active) setNeedsLegal(false);
        }
      })();
    } else if (!isClient) {
      setNeedsLegal(false);
    } else {
      setNeedsLegal(false);
    }
    return () => {
      active = false;
    };
  }, [profile?.legal_document_approval_needed, isClient, profile, needsLegal]);

  if (loading || !sessionChecked || needsLegal === null) {
    return <Loader />;
  }

  if (isClient && needsLegal) {
    // If not on client-profile with legal tab, redirect there
    if (location.pathname !== "/client-profile" || !location.search.includes("tab=legal")) {
      console.log("[route] LegalApprovalRoute → redirecting to legal tab");
      return <Navigate to="/client-profile?tab=legal" replace />;
    }
  }

  return <>{children}</>;
};

// Subscription Required Route Guard - redirects to payment page if subscription not active
const SubscriptionRequiredRoute = ({ children }: { children: React.ReactNode }) => {
  const { profile, isClient, loading, sessionChecked } = useAuth();
  const { hasActiveSubscription, loading: subscriptionLoading } = useSubscriptionStatus();
  const location = useLocation();

  if (loading || !sessionChecked || subscriptionLoading) {
    return <Loader />;
  }

  // Only enforce for clients who have completed onboarding
  if (isClient && profile?.onboarding_completed) {
    if (!hasActiveSubscription) {
      // Allow access to payment-required and payment-verification pages
      if (location.pathname !== "/payment-required" && location.pathname !== "/payment-verification") {
        console.log("[route] SubscriptionRequiredRoute → redirecting to payment-required");
        return <Navigate to="/payment-required" replace />;
      }
    }
  }

  return <>{children}</>;
};

// Admin Route Component
const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, isAdmin, isTrainer, loading, sessionChecked, rolesLoaded, rolesError, rolesFromCache } = useAuth();

  if (loading || !sessionChecked) {
    console.log("[route] AdminRoute → loader", { loading, sessionChecked, hasUser: !!user });
    return <Loader />;
  }
  if (!user) {
    console.log("[route] AdminRoute → /login");
    return <Navigate to="/login" replace />;
  }
  if (!rolesLoaded && !rolesError) {
    console.log("[route] AdminRoute → loader (roles pending)");
    return <Loader />;
  }
  if (rolesError && !rolesFromCache) {
    console.log("[route] AdminRoute → /auth-degraded");
    return <Navigate to="/auth-degraded" replace />;
  }
  if (!isAdmin && !isTrainer) {
    console.log("[route] AdminRoute → /client-portal", { isAdmin, isTrainer });
    return <Navigate to="/client-portal" replace />;
  }
  return <>{children}</>;
};

// Main Application Routes Component
const AppRoutes = () => {
  const location = useLocation();
  const isMobile = useIsMobile();

  useEffect(() => {
    try {
      console.debug("[route] →", location.pathname);
    } catch (error) {
      console.error("[route] Error logging pathname:", error);
    }
  }, [location.pathname]);

  useEffect(() => {
    try {
      applyDocumentMeta(location.pathname, location.search);
    } catch (error) {
      console.warn("[route] Failed to set document meta", error);
    }
  }, [location.pathname, location.search]);

  // Check if we should show mobile navbar (not on auth, onboarding, workout session, or admin pages)
  const showMobileNavbar =
    isMobile &&
    !["/", "/signup", "/login", "/onboarding", "/payment-verification", "/payment-required", "/start-workout", "/workout-session", "/admin", "/account-deactivated"].includes(
      location.pathname
    );

  return (
    <>
      <ScrollToTop />
      {showMobileNavbar && <MobileNavbar activeTab={getActiveTabFromPath(location.pathname)} />}
      <ActiveWorkoutIndicator />
      <PostWorkoutFeedbackWrapper />
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/about" element={<About />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/reset-password-confirm" element={<ResetPasswordConfirm />} />
        <Route path="/account-deactivated" element={<AccountDeactivated />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/post-login" element={<PostLogin />} />
        <Route path="/auth-degraded" element={<AuthDegraded />} />
        <Route path="/invited-user-setup" element={<InvitedUserSetup />} />
        <Route
          path="/onboarding"
          element={
            <OnboardingRoute>
              <ProtectedRoute>
                <ClientOnboarding />
              </ProtectedRoute>
            </OnboardingRoute>
          }
        />
        <Route
          path="/payment-verification"
          element={
            <ProtectedRoute>
              <PaymentVerification />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-required"
          element={
            <ProtectedRoute>
              <PaymentRequired />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-portal"
          element={
            <ProtectedRoute requireOnboarding disallowAdmin>
              <LegalApprovalRoute>
                <SubscriptionRequiredRoute>
                  <ClientPortal />
                </SubscriptionRequiredRoute>
              </LegalApprovalRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-profile"
          element={
            <ProtectedRoute requireOnboarding disallowAdmin>
              <LegalApprovalRoute>
                <SubscriptionRequiredRoute>
                  <ClientProfile />
                </SubscriptionRequiredRoute>
              </LegalApprovalRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/client-plan"
          element={
            <ProtectedRoute requireOnboarding disallowAdmin>
              <LegalApprovalRoute>
                <SubscriptionRequiredRoute>
                  <ClientPlan />
                </SubscriptionRequiredRoute>
              </LegalApprovalRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/explore"
          element={
            <ProtectedRoute requireOnboarding disallowAdmin>
              <LegalApprovalRoute>
                <SubscriptionRequiredRoute>
                  <ExploreHub />
                </SubscriptionRequiredRoute>
              </LegalApprovalRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assessments"
          element={
            <ProtectedRoute requireOnboarding disallowAdmin>
              <LegalApprovalRoute>
                <SubscriptionRequiredRoute>
                  <ExploreHub />
                </SubscriptionRequiredRoute>
              </LegalApprovalRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assessments/:assessmentId"
          element={
            <ProtectedRoute requireOnboarding disallowAdmin>
              <LegalApprovalRoute>
                <SubscriptionRequiredRoute>
                  <AssessmentEmbedPage />
                </SubscriptionRequiredRoute>
              </LegalApprovalRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/lesson/:lessonId"
          element={
            <ProtectedRoute requireOnboarding disallowAdmin>
              <LegalApprovalRoute>
                <SubscriptionRequiredRoute>
                  <LessonDetailPage />
                </SubscriptionRequiredRoute>
              </LegalApprovalRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/start-workout"
          element={
            <ProtectedRoute requireOnboarding disallowAdmin>
              <LegalApprovalRoute>
                <SubscriptionRequiredRoute>
                  <StartWorkout />
                </SubscriptionRequiredRoute>
              </LegalApprovalRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/workout-session"
          element={
            <ProtectedRoute requireOnboarding disallowAdmin>
              <LegalApprovalRoute>
                <SubscriptionRequiredRoute>
                  <WorkoutSession />
                </SubscriptionRequiredRoute>
              </LegalApprovalRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/view-workout/:templateId"
          element={
            <ProtectedRoute requireOnboarding disallowAdmin>
              <LegalApprovalRoute>
                <SubscriptionRequiredRoute>
                  <ViewWorkoutTemplate />
                </SubscriptionRequiredRoute>
              </LegalApprovalRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/chat"
          element={
            <ProtectedRoute requireOnboarding disallowAdmin>
              <LegalApprovalRoute>
                <SubscriptionRequiredRoute>
                  <ClientChat />
                </SubscriptionRequiredRoute>
              </LegalApprovalRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminPortal />
            </AdminRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
};

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <WorkoutProvider>
              <AppRoutes />
            </WorkoutProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
